import { ResponsiveBar } from "@nivo/bar";
import { startOfToday } from "date-fns";
import { useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";

import SROnly from "common/core/screen_reader";
import { Heading } from "common/core/typography";

import { ChartsSection } from "./shared/charts_section";
import { EmptyChart } from "./shared/empty_chart";
import type { NotarizeSentCompletedScalarHash } from "./shared/utils";

const TICKMARKS = 10;

const MESSAGES = defineMessages({
  chartsSectionHeading: {
    id: "8cf32e47-fd96-469b-bd51-add376b31e16",
    defaultMessage: "Sent vs. completed transactions",
  },
  sentChartTitle: {
    id: "ccd23d20-c129-47b0-8c9d-020a44054a87",
    defaultMessage: "Sent Transactions",
  },
  completedChartTitle: {
    id: "f39bcef9-2f21-4bcf-bd6d-3943bdd6beaa",
    defaultMessage: "Completed Transactions",
  },
  viewing6Months: {
    id: "025f6092-4804-4dd2-a526-e02cadf64701",
    defaultMessage: "Viewing {startDate} - {endDate} | Last 6 months",
  },
  emptySentChartTitle: {
    id: "bff3141e-3f15-400a-bc5e-dfb2b266c6f4",
    defaultMessage: "No sent transactions in the last 6 months",
  },
  emptySentChartDescription: {
    id: "56e1eaf8-e8cc-439e-b86f-ecbbef7dc190",
    defaultMessage: "Monthly sent transactions will display here.",
  },
  emptyCompletedChartTitle: {
    id: "73e645df-f8f5-4015-8a23-2c8146831ccf",
    defaultMessage: "No completed transactions in the last 6 months",
  },
  emptyCompletedChartDescription: {
    id: "2b79df11-accc-4b0d-b27c-5fecdb3f1d2c",
    defaultMessage: "Monthly completed transactions will display here.",
  },
  SROnlySentChartDescription: {
    id: "28bb33e9-e09c-429b-8845-bb9c726a9796",
    defaultMessage: "Monthly sent transactions chart with data for the last 6 months: {data}",
  },
  SROnlyCompletedChartDescription: {
    id: "62d9b017-6121-4884-9611-a5132b03239a",
    defaultMessage: "Monthly completed transactions chart with data for the last 6 months: {data}",
  },
});

const margin = { top: 25, right: 10, bottom: 50, left: 25 };
const axisLeftSettings = {
  legendOffset: -20,
  format: () => "",
};
const sharedProps = {
  enableLabel: false,
  enableTotals: true,
  indexBy: "label",
  margin,
};

export function SentCompletedSection({
  chartData,
  sixMonthsStartDate,
  sixMonthsEndDate,
}: {
  chartData: NotarizeSentCompletedScalarHash[];
  sixMonthsStartDate: string | null;
  sixMonthsEndDate: string | null;
}) {
  const intl = useIntl();

  const viewing6Months = useMemo(
    () =>
      intl.formatMessage(MESSAGES.viewing6Months, {
        startDate: sixMonthsStartDate
          ? new Date(sixMonthsStartDate).toLocaleDateString("default", {
              month: "short",
              year: "numeric",
            })
          : "",
        endDate: sixMonthsEndDate
          ? new Date(sixMonthsEndDate).toLocaleDateString("default", {
              month: "short",
              year: "numeric",
            })
          : "",
      }),
    [intl, sixMonthsStartDate, sixMonthsEndDate],
  );

  const last6Months = useMemo(
    () =>
      Array.from({ length: 6 }).map((_, i) => {
        const d = new Date(startOfToday().getFullYear(), startOfToday().getMonth() - (5 - i), 1);
        return { month: d.getMonth(), year: d.getFullYear() };
      }),
    [],
  );

  // Combine safeChartData, maxSentValue, and maxCompletedValue in a single useMemo
  const { safeChartData, maxSentValue, maxCompletedValue } = useMemo(() => {
    let maxSent = 0;
    let maxCompleted = 0;
    const data = last6Months
      .map(({ month, year }) => {
        const match = chartData.find((d) => {
          const date = new Date(d.label);
          return date.getUTCFullYear() === year && date.getUTCMonth() === month;
        });
        const sent = match?.sent_count ? Number(match.sent_count) : 0;
        const completed = match?.completed_count ? Number(match.completed_count) : 0;
        if (sent > maxSent) {
          maxSent = sent;
        }
        if (completed > maxCompleted) {
          maxCompleted = completed;
        }
        return {
          label: new Date(year, month, 1).toLocaleString(intl.locale, { month: "short" }),
          sent,
          completed,
        };
      })
      .slice(-6); // Ensure we only take the last 6 months
    return { safeChartData: data, maxSentValue: maxSent, maxCompletedValue: maxCompleted };
  }, [chartData, last6Months]);

  const sentTickValuesToShow = useMemo(() => {
    if (maxSentValue <= 1) {
      return [0, 1];
    }
    const step = maxSentValue / TICKMARKS;
    const ticks = Array.from({ length: TICKMARKS + 1 }, (_, i) => Number((step * i).toFixed(2)));
    // Remove duplicates (in case of very small maxSentValue)
    return Array.from(new Set(ticks));
  }, [maxSentValue]);

  const completedTickValuesToShow = useMemo(() => {
    if (maxCompletedValue <= 1) {
      return [0, 1];
    }
    const step = maxCompletedValue / TICKMARKS;
    const ticks = Array.from({ length: TICKMARKS + 1 }, (_, i) => Number((step * i).toFixed(2)));
    return Array.from(new Set(ticks));
  }, [maxCompletedValue]);

  return (
    <ChartsSection
      heading={intl.formatMessage(MESSAGES.chartsSectionHeading)}
      description={viewing6Months}
      firstChart={
        <>
          {maxSentValue <= 0 ? (
            <EmptyChart
              heading={intl.formatMessage(MESSAGES.emptySentChartTitle)}
              description={intl.formatMessage(MESSAGES.emptySentChartDescription)}
            />
          ) : (
            <>
              <Heading level="h3" textStyle="allCapsLabelSmall">
                {intl.formatMessage(MESSAGES.sentChartTitle)}
              </Heading>
              <SROnly>
                {intl.formatMessage(MESSAGES.SROnlySentChartDescription, {
                  data: JSON.stringify(
                    safeChartData.map(({ label, sent }) => ({
                      label: new Date(label).toLocaleString(intl.locale, { month: "long" }),
                      sent,
                    })),
                  ),
                })}
              </SROnly>
              <ResponsiveBar
                data={safeChartData}
                keys={["sent"]}
                gridYValues={sentTickValuesToShow}
                axisLeft={{
                  tickValues: sentTickValuesToShow,
                  ...axisLeftSettings,
                }}
                colors="var(--primary-50)"
                {...sharedProps}
              />
            </>
          )}
        </>
      }
      secondChart={
        maxCompletedValue <= 0 ? (
          <EmptyChart
            heading={intl.formatMessage(MESSAGES.emptyCompletedChartTitle)}
            description={intl.formatMessage(MESSAGES.emptyCompletedChartDescription)}
          />
        ) : (
          <>
            <Heading level="h3" textStyle="allCapsLabelSmall">
              {intl.formatMessage(MESSAGES.completedChartTitle)}
            </Heading>
            <SROnly>
              {intl.formatMessage(MESSAGES.SROnlyCompletedChartDescription, {
                data: JSON.stringify(
                  safeChartData.map(({ label, completed }) => ({
                    label: new Date(label).toLocaleString(intl.locale, { month: "long" }),
                    completed,
                  })),
                ),
              })}
            </SROnly>
            <ResponsiveBar
              data={safeChartData}
              keys={["completed"]}
              gridYValues={completedTickValuesToShow}
              axisLeft={{
                tickValues: completedTickValuesToShow,
                ...axisLeftSettings,
              }}
              colors="var(--success-80)"
              {...sharedProps}
            />
          </>
        )
      }
    />
  );
}
